import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-input-2/lib/material.css';
import axios from 'axios';

// const phoneRegExp = /^[0-9]{10}$/;

const validationSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .test(
      'is-valid-phone',
      'Invalid phone number format',
      (value) => value && isValidPhoneNumber(value)
    )
    .required('Phone number is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  alternatePhoneNumber: Yup.string().test(
    'is-valid-phone',
    'Invalid phone number',
    (value) => !value || isValidPhoneNumber(value)
  ),
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State/Province is required'),
  city: Yup.string().required('City is required'),
  streetAddress: Yup.string().required('Street address is required'),
  postalCode: Yup.string().required('Postal code is required')
});

const HospitalContactInfo = ({ initialData = {}, onComplete, onBack }) => {
  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const csrfToken = getCookie('csrftoken');

  // Set the CSRF token in the default headers
  axios.defaults.headers.common['X-CSRFToken'] = csrfToken;

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    street_address: '',
    postal_code: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('email', formData.email);
    data.append('phone', formData.phone);
    data.append('country', formData.country);
    data.append('state', formData.state);
    data.append('city', formData.city);
    data.append('street_address', formData.street_address);
    data.append('postal_code', formData.postal_code);

    try {
      const response = await axios.post('http://127.0.0.1:8000/hospital/contact', data, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('Success:', response.data);
      if (response.status === 201) {
        setSuccess('Contact information submitted successfully!!');
        setError(''); // Reset error on successful submission
        setFormData([]); // Clear the form fields

        onComplete({
          email: values.email,
          phone: values.phone,
          country: values.country,
          state: values.state,
          city: values.city,
          street_address: values.profilePicture,
          postal_code: values.postal_code
        }, true); // Pass true for isValid
      } else {
        throw new Error('Contact information submission failed. Please try again.');
      }
    } catch (error) {
      console.log('');
    }
  };

  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Contact Information</h2>
      {error && <div className={css(styles.error)}>{error}</div>}
      {success && <div className={css(styles.success)}>{success}</div>}
      <Formik
        initialValues={{
          phoneNumber: initialData?.phoneNumber || '',
          email: initialData?.email || '',
          alternatePhoneNumber: initialData?.alternatePhoneNumber || '',
          country: initialData?.country || '',
          state: initialData?.state || '',
          city: initialData?.city || '',
          streetAddress: initialData?.streetAddress || '',
          postalCode: initialData?.postalCode || ''
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='email' className={css(styles.label)}>
                Email
              </label>
              <input
                name='email'
                type='email'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.email && touched.email && (
                <div className={css(styles.error)}>{errors.email}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='phoneNumber' className={css(styles.label)}>
                Phone Number
              </label>
              <input
                name='phone'
                type='text'
                onChange={handleChange}
                placeholder='e.g. +XX 123 456 789'
                inputClass={css(styles.phoneInput)}
                className={css(styles.input)}
                required
              />
              {errors.phoneNumber && touched.phoneNumber && (
                <div className={css(styles.error)}>{errors.phoneNumber}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label
                htmlFor='alternatePhoneNumber'
                className={css(styles.label)}
              >
                Alternate Phone Number (Optional)
              </label>
              <input
                type='text'
                onChange={handleChange}
                placeholder='e.g. +XX 987 654 321'
                inputClass={css(styles.phoneInput)}
                className={css(styles.input)}
              />
              {errors.alternatePhoneNumber && touched.alternatePhoneNumber && (
                <div className={css(styles.error)}>
                  {errors.alternatePhoneNumber}
                </div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='country' className={css(styles.label)}>
                Country
              </label>
              <input
                name='country'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.country && touched.country && (
                <div className={css(styles.error)}>{errors.country}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='state' className={css(styles.label)}>
                State/Province
              </label>
              <input
                name='state'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.state && touched.state && (
                <div className={css(styles.error)}>{errors.state}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='city' className={css(styles.label)}>
                City
              </label>
              <input
                name='city'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.city && touched.city && (
                <div className={css(styles.error)}>{errors.city}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='streetAddress' className={css(styles.label)}>
                Street Address
              </label>
              <input
                name='street_address'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.streetAddress && touched.streetAddress && (
                <div className={css(styles.error)}>{errors.streetAddress}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='postalCode' className={css(styles.label)}>
                Postal Code
              </label>
              <input
                name='postal_code'
                type='text'
                onChange={handleChange}
                className={css(styles.input)}
                required
              />
              {errors.postalCode && touched.postalCode && (
                <div className={css(styles.error)}>{errors.postalCode}</div>
              )}
            </div>
            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
                  styles.button,
                  styles.nextButton
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSubmit}
              >
                Save
              </motion.button>
              <motion.button
                type='button'
                className={css(
                  styles.button,
                  styles.nextButton
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (!isValid) {
                    console.log('Form is invalid, staying on the same page.');
                    alert('Please fill in all required fields before proceeding.');
                    return;
                  }
                  console.log('Form is valid, navigating to the next section.');
                  onComplete(); // Ensure this function correctly updates the state for the next section
                }}
              >
                Next
              </motion.button>
              <motion.button
                type='button'
                className={css(styles.button, styles.backButton)}
                onClick={onBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Back
              </motion.button>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    color: '#4a5568'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  error: {
    color: '#DC2626',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '1rem'
  },
  nextButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  },
  phoneInput: {
    width: '100%',
    display: 'flex !important',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem !important',
    border: '1px solid #E5E7EB',
    borderRadius: '0.375rem',
    '& .special-label': {
      display: 'none'
    },
    '& .selected-flag': {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      backgroundColor: 'transparent !important',
      '&:hover, &:focus': {
        backgroundColor: 'transparent !important'
      }
    },
    '& .selected-flag::after': {
      content: '""',
      width: '1px',
      height: '1.5rem',
      backgroundColor: '#E5E7EB',
      marginLeft: '0.75rem'
    },
    '& .form-control': {
      border: 'none !important',
      background: 'transparent !important',
      width: '100% !important',
      padding: '0 !important',
      '&:focus': {
        outline: 'none',
        boxShadow: 'none'
      }
    }
  }
});

export default HospitalContactInfo;
